import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  Divider,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import {
  PlayArrow as TestIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  OpenInNew as OpenIcon,
  DeleteSweep as PurgeIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';
import { formatDateTime } from '../../utils/dateUtils';
import { verdictMeta } from '../Intel/verdict';
import { describeApiError } from './agentMeta';

// geoip and dns never accept a key; the other providers all support one (required or optional).
const KEYLESS_PROVIDERS = new Set(['geoip', 'dns']);

const formatHHMM = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const statusMeta = (provider) => {
  if (!provider.enabled) return { label: 'Disabled', color: 'default' };
  if (!provider.ready) return { label: 'Needs key', color: 'warning' };
  return { label: 'Ready', color: 'success' };
};

/** One provider's editable card: enable switch, API key entry, save/test actions. */
const ProviderCard = ({ provider, canManage, onChange, notify }) => {
  const [enabled, setEnabled] = useState(!!provider.enabled);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { setEnabled(!!provider.enabled); }, [provider.enabled]);

  const status = statusMeta(provider);
  const cooling = !!(provider.cooldownUntil && new Date(provider.cooldownUntil) > new Date());

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { enabled };
      if (apiKeyInput.trim()) payload.apiKey = apiKeyInput.trim();
      await apiService.enrichment.updateProvider(provider.id, payload);
      setApiKeyInput('');
      notify.success(`${provider.label}: settings saved`);
      await onChange();
    } catch (err) {
      notify.error(`${provider.label}: ${describeApiError(err, 'Failed to save provider').message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKey = async () => {
    try {
      setSaving(true);
      await apiService.enrichment.updateProvider(provider.id, { apiKey: null });
      setApiKeyInput('');
      notify.success(`${provider.label}: API key cleared`);
      await onChange();
    } catch (err) {
      notify.error(`${provider.label}: ${describeApiError(err, 'Failed to clear API key').message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const res = await apiService.enrichment.testProvider(provider.id);
      setTestResult({ success: true, ...res.data });
    } catch (err) {
      const data = err.response && err.response.data;
      setTestResult({ success: false, message: (data && (data.message || data.error)) || describeApiError(err, 'Test failed').message });
    } finally {
      setTesting(false);
      await onChange();
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
          <Box sx={{ flexGrow: 1, minWidth: 220 }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{provider.label}</Typography>
              <Chip size="small" color={status.color} label={status.label} />
              <Chip size="small" variant="outlined" label={provider.free ? 'Free' : 'API key required'} />
              {cooling && (
                <Tooltip title="This provider is throttling requests until its cooldown clears">
                  <Chip size="small" color="warning" label={`Cooling down until ${formatHHMM(provider.cooldownUntil)}`} />
                </Tooltip>
              )}
              {provider.authError && (
                <Tooltip title={`${provider.authError.message || 'API key rejected'} — save a new key or run Test to clear`}>
                  <Chip size="small" color="error" label={`Key rejected (HTTP ${provider.authError.status || 401})`} />
                </Tooltip>
              )}
              {provider.docsUrl && (
                <Tooltip title="Provider documentation">
                  <IconButton size="small" component={Link} href={provider.docsUrl} target="_blank" rel="noopener noreferrer">
                    <OpenIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {provider.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{provider.description}</Typography>
            )}
            {!!provider.rpm && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                ≤ {provider.rpm} requests/min
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {(provider.supports || []).map((t) => <Chip key={t} size="small" variant="outlined" label={t.toUpperCase()} />)}
            </Stack>
          </Box>
          <Tooltip title={enabled ? 'Disable provider' : 'Enable provider'}>
            <span>
              <Switch
                checked={enabled}
                disabled={!canManage || saving}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            </span>
          </Tooltip>
        </Box>

        {!KEYLESS_PROVIDERS.has(provider.id) && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="password"
              label="API key"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={provider.hasApiKey ? 'configured' : ''}
              helperText={provider.hasApiKey ? 'Leave blank to keep the existing key' : provider.requiresKey ? 'Required to enable this provider' : 'Optional — leave blank to use the free tier'}
              disabled={!canManage || saving}
              sx={{ minWidth: 260 }}
            />
            {provider.hasApiKey && (
              <Tooltip title="Clear stored API key">
                <span>
                  <Button size="small" color="inherit" startIcon={<ClearIcon />} onClick={handleClearKey} disabled={!canManage || saving}>
                    Clear key
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!canManage || saving}
          >
            Save
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={handleTest}
            disabled={!canManage || testing}
          >
            Test
          </Button>
          <Typography variant="caption" color="text.secondary">
            Lookups: {(provider.stats && provider.stats.lookups) || 0} · Errors: {(provider.stats && provider.stats.errors) || 0}
            {(provider.stats && provider.stats.rateLimited) > 0 ? ` · rate limited: ${provider.stats.rateLimited}` : ''}
          </Typography>
          {provider.lastTest && (
            <Typography variant="caption" color={provider.lastTest.success ? 'text.secondary' : 'error'}>
              Last test: {provider.lastTest.success ? 'success' : `failed – ${provider.lastTest.error || 'unknown error'}`} ({formatDateTime(provider.lastTest.at)})
            </Typography>
          )}
        </Box>

        {testResult && (
          <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1.5 }} onClose={() => setTestResult(null)}>
            {testResult.success
              ? `OK in ${testResult.responseTime ?? '?'}ms${testResult.sample ? ` – sample verdict: ${testResult.sample.verdict}${testResult.sample.summary ? ` (${testResult.sample.summary})` : ''}` : ''}`
              : (testResult.message || 'Test failed')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

/** Threat-intel provider configuration + lookup cache management. */
const ThreatIntelTab = () => {
  const { canManageAgents } = usePermission();
  const [providers, setProviders] = useState([]);
  const [cache, setCache] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [purging, setPurging] = useState(false);

  const notify = {
    success: (msg) => { setNotice(msg); setError(null); },
    error: (msg) => { setError(msg); setNotice(null); },
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [providersRes, cacheRes] = await Promise.all([
        apiService.enrichment.getProviders(),
        apiService.enrichment.getCache(),
      ]);
      setProviders(providersRes.data.providers || []);
      setCache(cacheRes.data);
      setError(null);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load threat-intel providers').message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePurge = async () => {
    if (!window.confirm('Purge the entire threat-intel lookup cache? Future lookups will re-query providers.')) return;
    try {
      setPurging(true);
      const res = await apiService.enrichment.purgeCache();
      setNotice(`Purged ${res.data.deleted} cached lookup(s)`);
      setError(null);
      await load();
    } catch (err) {
      setError(describeApiError(err, 'Failed to purge cache').message);
    } finally {
      setPurging(false);
    }
  };

  if (loading && providers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>Threat intelligence sources</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the providers used to enrich indicators, detections and ad-hoc lookups.
        {!canManageAgents && ' You have read-only access to these settings.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Grid container spacing={2}>
        {providers.map((provider) => (
          <Grid item xs={12} md={6} key={provider.id}>
            <ProviderCard provider={provider} canManage={canManageAgents} onChange={load} notify={notify} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1}>
        <Typography variant="h6" component="h2">Lookup cache</Typography>
        <Tooltip title={canManageAgents ? 'Delete every cached lookup result' : 'Requires agent management permission'}>
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={purging ? <CircularProgress size={16} /> : <PurgeIcon />}
              onClick={handlePurge}
              disabled={!canManageAgents || purging || !cache || !cache.count}
            >
              Purge cache
            </Button>
          </span>
        </Tooltip>
      </Box>

      {cache && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{cache.count} cached result(s)</Typography>
            {Object.entries(cache.byVerdict || {}).map(([verdict, count]) => {
              const meta = verdictMeta(verdict);
              return <Chip key={verdict} size="small" color={meta.color} label={`${meta.label}: ${count}`} />;
            })}
          </Stack>

          {cache.recent && cache.recent.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Verdict</TableCell>
                  <TableCell>Checked</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cache.recent.map((r) => {
                  const meta = verdictMeta(r.verdict);
                  return (
                    <TableRow key={`${r.type}-${r.value}`}>
                      <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.value}</TableCell>
                      <TableCell>{r.type}</TableCell>
                      <TableCell><Chip size="small" color={meta.color} label={meta.label} /></TableCell>
                      <TableCell>{formatDateTime(r.checkedAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No cached lookups yet.</Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ThreatIntelTab;
